/** How the web app reaches logs that send no CORS headers. */
export type ProxyMode =
  | 'ask' // undecided: offer the built-in proxy the first time a log blocks the browser
  | 'builtin' // the proxy run by Merkle Lens (only where this site is served from merklens.app)
  | 'custom' // the user's own proxy URL
  | 'off';

export interface Settings {
  proxy: ProxyMode;
  /** The user's own CORS proxy base URL (used when proxy === 'custom'). */
  proxyUrl: string;
  /** Enabled built-in log-list sources (ids from BUILTIN_SOURCES). */
  sources: string[];
  /** Optional extra log list (log_list_v3 schema, must send CORS headers). */
  customListUrl: string;
  showRetired: boolean;
  theme: 'auto' | 'light' | 'dark';
}

export const DEFAULT_SETTINGS: Settings = { proxy: 'ask', proxyUrl: '', sources: ['google', 'apple'], customListUrl: '', showRetired: false, theme: 'auto' };

/** Merges stored settings over the defaults; older shapes (a bare proxyUrl, a contact e-mail) are carried over or dropped. */
export function migrateSettings(raw: unknown): Settings {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out: Settings = { ...DEFAULT_SETTINGS };
  if (typeof r.proxyUrl === 'string') out.proxyUrl = r.proxyUrl;
  const modes: ProxyMode[] = ['ask', 'builtin', 'custom', 'off'];
  if (modes.includes(r.proxy as ProxyMode)) out.proxy = r.proxy as ProxyMode;
  else if (out.proxyUrl.trim() !== '') out.proxy = 'custom'; // v2 settings: a URL meant "use it"
  if (Array.isArray(r.sources) && r.sources.every((x) => typeof x === 'string')) out.sources = r.sources as string[];
  if (typeof r.customListUrl === 'string') out.customListUrl = r.customListUrl;
  if (typeof r.showRetired === 'boolean') out.showRetired = r.showRetired;
  if (r.theme === 'auto' || r.theme === 'light' || r.theme === 'dark') out.theme = r.theme;
  return out;
}
